/**
 * The public tour API (/api/public/tour). No session and no credentials: a
 * prospect is not a resident. The manage token goes in a POST body, never in
 * a URL.
 */
import { readAttribution } from '../lib/attribution';
import type { PublicBrand } from '@tendedloop/shared/brand-public';
import { API_BASE } from '@/lib/api';
import type { TourLang } from './strings';

export interface TourSlot {
  date: string;
  start: string;
  end: string;
  free: boolean;
}

export interface TourPageData {
  brand: PublicBrand;
  handle: string;
  unitTypes: { id: string; name: string }[];
  slots: TourSlot[];
  timeZone: string;
  consentVersion: string;
  privacyPolicyUrl: string | null;
  pendingMinutes: number;
}

export type TourLinkStatus = 'pending' | 'expired' | 'confirmed' | 'cancelled' | 'past';

export interface TourLinkView {
  status: TourLinkStatus;
  start: string;
  end: string;
  timeZone: string;
  unitTypeName: string | null;
  brand: PublicBrand | null;
  handle: string | null;
  canCancel: boolean;
}

export type PageAnswer =
  | { kind: 'page'; page: TourPageData }
  | { kind: 'redirect'; handle: string }
  | { kind: 'not-found' }
  | { kind: 'error' };

export async function fetchTourPage(handle: string, lang: TourLang): Promise<PageAnswer> {
  try {
    const res = await fetch(`${API_BASE}/public/tour/${encodeURIComponent(handle)}?lang=${lang}`, {
      credentials: 'omit',
    });
    if (res.status === 404) return { kind: 'not-found' };
    if (!res.ok) return { kind: 'error' };
    const data = (await res.json())?.data;
    if (data && typeof data.redirect === 'string') return { kind: 'redirect', handle: data.redirect };
    if (data && Array.isArray(data.slots)) return { kind: 'page', page: data as TourPageData };
    return { kind: 'error' };
  } catch {
    return { kind: 'error' };
  }
}

export interface BookInput {
  slotStart: string;
  name: string;
  email: string;
  phone?: string;
  unitTypeId?: string;
  marketingOptIn: boolean;
  consentVersion: string;
  turnstileToken?: string;
  website: string;
  lang: TourLang;
}

export type Answer<T> = { ok: true; data: T } | { ok: false; status: number; code: string };

async function post<T>(path: string, body: unknown): Promise<Answer<T>> {
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'omit',
    });
    const json = await res.json().catch(() => null);
    if (res.ok) return { ok: true, data: json?.data as T };
    const code = res.status === 429 ? 'RATE_LIMITED' : typeof json?.code === 'string' ? json.code : 'DEFAULT';
    return { ok: false, status: res.status, code };
  } catch {
    return { ok: false, status: 0, code: 'DEFAULT' };
  }
}

export const bookTour = (handle: string, input: BookInput) => {
  // The ad that brought the visit (first touch), kept on the new lead.
  const attribution = readAttribution();
  return post<{ status: 'check_email' }>(`/public/tour/${encodeURIComponent(handle)}/book`, {
    ...input,
    ...(attribution ? { attribution } : {}),
  });
};

export const tourLink = (action: 'view' | 'confirm' | 'cancel', token: string, lang: TourLang) =>
  post<TourLinkView>(`/public/tour/link${action === 'view' ? '' : `/${action}`}?lang=${lang}`, { token });
